import type { ClientQuestion, LocaleCode } from '@fixit/bozp-content';
import { resolveLocalized } from '../locale';

export interface OrderingQuizProps {
  question: Extract<ClientQuestion, { kind: 'ordering' }>;
  value: string[] | undefined;
  onChange: (value: string[]) => void;
  locale: LocaleCode;
}

/**
 * kind: 'ordering' — emits a string[] of item ids in the learner's chosen order
 * (order-sensitive server-side). Move-up/move-down controls keep the widget
 * keyboard-accessible without a drag library. Defaults to the authored choice
 * order when uncontrolled.
 */
export function OrderingQuiz({ question, value, onChange, locale }: OrderingQuizProps) {
  const choices = question.choices ?? [];
  const order = value ?? choices.map((c) => c.id);
  const labelOf = (id: string) =>
    resolveLocalized(choices.find((c) => c.id === id)?.label ?? { 'cs-CZ': id }, locale);

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <div data-quiz-kind="ordering">
      <p>{resolveLocalized(question.prompt, locale)}</p>
      <ol>
        {order.map((id, index) => {
          const label = labelOf(id);
          return (
            <li key={id} data-item-id={id}>
              {label}
              <button
                type="button"
                aria-label={`Posunout nahoru: ${label}`}
                onClick={() => move(index, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                aria-label={`Posunout dolů: ${label}`}
                onClick={() => move(index, 1)}
              >
                ↓
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
